// クラシック版のキャラの読み込みと、中身（charat.core）へのつなぎ（段階 C の2 の 7c。2026-10-26 に chibi.js と画面ファイル gen1b.js の reverseAll から描き直した。動きは前と同じ）。
// 組み立て（makeCharactor → makeCharactorCore）・ランダム・初めのキャラ・プリセット・中身への受け渡し（core・coreOps・位置と拡大の動き）・全レイヤーを初めの値に・
// 保存データの並びの直し（SAVE_FIELDS）・第1世代の柄（mono）と線の太さの掛け直し・手の排他の掛け直し・切り抜き・全体の反転・柄と色の入れかえの掛け直し・線の太さ・グループ・全画面。
// chibi に置く。d3 の選択・動き（transition・attrTween）と jQuery は fx.d3any・fx.jq でそのまま（7 で自前に）
import { chibi, common } from './engine.js';
import { screen } from './colors.js';
import * as fx from './fx.js';
import { S } from './sel.js';
import { easeByName } from './ease.js';
import * as jq from './jq.js';

type Engine = Record<string, any>;
const E = (): Engine => chibi() as unknown as Engine;
const C = (): Record<string, any> => common() as unknown as Record<string, any>;
const draw = (): any => (window as unknown as { charatDraw: unknown }).charatDraw;
const core = (): any => (window as unknown as { charatCore?: unknown }).charatCore;

// ---- 柄と色の入れかえ・線の太さ・グループ ----

/**
 * パーツを替えたあと・読み込みで、レイヤーの柄と色の入れかえを掛け直す（描き方は charat.draw.js の patternLine）。
 * 柄の無いメーカーは柄の一覧が null なので、柄の番号があっても貼らない。第1世代の柄（mono の gen1-pattern.js）はこの関数ごと自前の形に替える（替えたかは setPartsColorPatternStd と比べる）
 */
export function setPartsColorPattern(aSvgImage: any, aCategory: any, aPtn: string, aPtnRev: number, aPtnMode: number, aColor: string): void {
  const e = E();
  const layers = aCategory.LAYER.map((d: string) => aSvgImage.select('#' + e.ATTR_LAYER + d).node());
  const defs = document.querySelector('#' + (aCategory.LAYER[0] != e.PARTS_BG ? e.SVG_SAVE_DEF : e.SVG_BGSAVE_DEF));
  const options = {
    defs,
    list: e.patternList,
    size: e.SIZE_PATTERN,
    adjust: e.ADJUST_PATTERN,
    ex1: e.COLOR_EX1,
    ex2: e.COLOR_EX2,
    stroke1: e.COLOR_STROKE1,
    stroke2: e.COLOR_STROKE2,
    partsColor: e.PATTERN_PARTS_COLOR,
    revParts: e.PATTERN_REV_PARTS,
  };
  if (draw().patternLine(layers, aCategory, aPtn, aPtnRev, aPtnMode, aColor, options)) {
    // 柄を貼ったら、カテゴリの全レイヤーに柄の値を入れる
    aCategory.LAYER.forEach((layerName: string) => {
      (layerName == e.PARTS_BG ? e.svgBg : e.svgMake).select('#' + e.ATTR_LAYER + layerName).attr('paramPtn', aPtn).attr('paramPtnMode', aPtnMode).attr('paramPrev', aPtnRev);
    });
  }
}
/** 今の線の太さ（太さが入っている最初のカテゴリの値。どこにも無ければ SIZE_WORG。属性の文字のことがある） */
export function getValidStrokeWidth(): unknown {
  const e = E();
  let width: unknown = e.SIZE_WORG;
  for (const key in e.allCategory) {
    const sw = e.svgMake.select('#' + e.ATTR_LAYER + e.allCategory[key].LAYER[0]).attr('paramSW');
    if (sw != undefined && sw !== '') {
      width = sw;
      break;
    }
  }
  return width;
}
export const getGroupCategory = (aCategory: unknown, aSel: unknown): unknown => draw().groupCategories(aCategory, aSel);
export const getExcludeCategory = (aCategory: unknown, aSel: unknown): unknown => draw().excludeCategories(aCategory, aSel);

// ---- 読み込み ----

/** ランダムなキャラ（全カテゴリ） */
export function loadRandom(): void {
  const e = E();
  const csel = { v: e.ATTR_SAVEVER, n: [] as string[], c: [] as string[] };
  for (const key in e.allCategory) csel.c.push(key);
  e.makeCharactor(csel, true, true);
}
/**
 * 初めのキャラ。1) 画像からの復元（restore.php が sessionStorage の avatarData に入れた圧縮文字列）2) オートセーブ 3) メーカーの初めのキャラ（charaDef の先頭）、無ければ決まったキャラ。
 * 注意: 復元データが壊れていると例外で止まり、removeItem まで届かない（前のまま）
 */
export function loadDefault(): void {
  const e = E();
  const avatarData = sessionStorage.getItem('avatarData');
  if (avatarData) {
    const restoredChara = JSON.parse(C().zipStrToStr(avatarData));
    e.makeCharactor(restoredChara, false, true);
    sessionStorage.removeItem('avatarData');
  } else if (!e.loadChara(e.ATTR_AUTOSAVEKEY, false)) {
    const cdata =
      e.charaDef != null
        ? JSON.parse(e.charaDef[0][3])
        : {
            v: e.ATTR_SAVEVER,
            n: [],
            c: ['bg,001,,,,', 'ground,001,,,,', 'hair3,002,,,,', 'hair2,001,,,,', 'base,001,,,,', 'bottoms,002,,,,', 'tops,002,,,,', 'face,002,,,,', 'make,002,,,,', 'mouth,001,,,,', 'eye,001,,,,', 'hair1,001,,,,', 'eyeblow,002,,,,'],
          };
    e.makeCharactor(cdata, false, true);
  }
}
/** プリセットのキャラ（キャラの一覧の番号） */
export function load(anId: unknown): void {
  const e = E();
  const cdata = e.getChara(anId);
  if (cdata.id == anId) {
    C().setNowMaking(true, 1, 0, 0, e.DIV_F_CONTENTS);
    C().setNowMaking(false, 1, 1200, 300, e.DIV_F_CONTENTS);
    e.makeCharactor(JSON.parse(cdata.json), false, true);
  }
}
/** プリセットのキャラのデータ（無ければ id は ""） */
export function getChara(anId: unknown): Record<string, unknown> {
  const e = E();
  const cdata: Record<string, unknown> = { id: '' };
  for (let i = 0; i < e.charaDef.length; i++) {
    if (e.charaDef[i][0] == anId) {
      cdata.id = e.charaDef[i][0];
      cdata.name = e.charaDef[i][1];
      cdata.mode = e.charaDef[i][2];
      cdata.json = e.charaDef[i][3];
      break;
    }
  }
  return cdata;
}
/** PARTS_MASK のレイヤーの形で PARTS_MASKED のレイヤーを切り抜く（nyanko3 のフェイスペイント） */
export function applyMask(aSvg: unknown, aLayerKey: string): void {
  const e = E();
  const masked = e.PARTS_MASKED.map((name: string) => e.allCategory[name]);
  C().changeMask(aSvg, S.select('#' + e.SVG_SAVE + ' defs'), aLayerKey, e.allCategory[e.PARTS_MASK], masked);
}
/** 保存データの 1 行（split 済み）を共通の並びに（SAVE_FIELDS のメーカーだけ） */
export const toStdSaveLine = (aCols: string[]): string[] => draw().toStdSaveLine(aCols, E().SAVE_FIELDS);

// ---- 中身（charat.core） ----

/**
 * 中身（window.charatCore）につなぐ（はじめの 1 回だけ attach）。中身が読めていなければ null（キャラは組み立てられない）。
 * 背景のレイヤーだけ別の SVG（chibi.svgBg）にある（g#lcart_bg は chibi.svgMake にも同じ名前の空のものがあるので、背景の SVG を先に見る）
 */
export function coreAttach(): unknown {
  const e = E();
  if (!core()) return null;
  if (!e.coreAttached) {
    core().attach({
      data: e.categoryData,
      partsCache: e.partsCache,
      svg: document.getElementById(e.SVG_SAVE),
      find: (aLayerName: string) => E().svgBg.select('#' + E().ATTR_LAYER + aLayerName).node() || E().svgMake.select('#' + E().ATTR_LAYER + aLayerName).node(),
      scale: e.scMake.x(e.SIZE_CHARAT_IMAGE) / e.SIZE_CHARAT_IMAGE,
      bgLayer: e.PARTS_BG,
      keepMove: e.config('KEEP_MOVE_ON_PARTS_CHANGE', false),
      hooks: { layerTransform: e.coreLayerTransform, zoomTransform: e.coreZoomTransform },
      // 柄: 一覧・defs・描き方はエンジンのもの。柄の無いメーカーは一覧が null で貼らない（一覧は配列にして渡す）
      pattern: {
        list: e.patternList ? Array.prototype.slice.call(e.patternList) : null,
        defs: (aCategory: any) => document.querySelector('#' + (aCategory.LAYER[0] != E().PARTS_BG ? E().SVG_SAVE_DEF : E().SVG_BGSAVE_DEF)),
        partsColor: e.PATTERN_PARTS_COLOR,
        revParts: e.PATTERN_REV_PARTS,
        revMax: e.MODE_COLREV,
        charaLayer: e.PARTS_CHARA,
        engineDraws: e.setPartsColorPattern !== e.setPartsColorPatternStd,
      },
    });
    e.coreAttached = true;
  }
  return core();
}
/** 中身の操作（組み立てたあとだけ。前は null） */
export const coreOps = (): unknown => (E().coreAttached ? core().classic : null);

/**
 * 中身がレイヤーの g に位置・反転・回転を付けるときの動き。rev: 反転の前の位置から横の位置と倍率だけを直線で（REVERSE_EASE。run は REVERSE_HOP で跳ねる）、
 * move: すぐ（動きの途中を止めるため d3 の 0ms）、reset: 150ms。読み込みなどはその場で
 */
export function coreLayerTransform(aG: Element, p: any, aWhy: string, _aCategory: unknown, _aLayerName: string, info: any): void {
  const e = E();
  const endTransform = draw().layerTransform(p.x, p.y, p.sx, p.sy, p.rot, p.c);
  if (aWhy === 'rev' && info.prev) {
    const delay = info.delay == undefined ? 0 : info.delay;
    const duration = info.duration == undefined ? 150 : info.duration;
    const x0 = info.prev.x;
    const oldScx = info.prev.sx;
    if (e.config('REVERSE_HOP', false)) {
      // 向きを変える前の側の位置で、画面の高さの 1 割だけ上へ（ずらす幅は画面の座標）
      const frame = e.scMake.x(e.SIZE_CHARAT_IMAGE) - e.scMake.x(0);
      const hopX = p.sx < 0 ? p.x - frame : p.x + frame;
      const hop = e.svgMake.attr('height') * 0.1;
      const hopY = p.y - (e.scMake.y(hop) - e.scMake.y(0));
      S
        .select(aG)
        .transition()
        .delay(delay)
        .duration(duration / 2)
        .ease(S.easeBackOut)
        .attr('transform', draw().layerTransform(hopX, hopY, oldScx, p.sy, p.rot, p.c))
        .transition()
        .delay(delay)
        .duration(duration * 1.5)
        .ease(S.easeBackOut)
        .attr('transform', endTransform);
      return;
    }
    S
      .select(aG)
      .transition()
      .delay(delay)
      .duration(duration)
      .ease(easeByName(e.config('REVERSE_EASE', 'easeBackOut')) || S.easeBackOut)
      .attrTween('transform', () => (t: number) => {
        if (t === 1) return endTransform;
        return draw().layerTransform(x0 + (p.x - x0) * t, p.y, oldScx + (p.sx - oldScx) * t, p.sy, p.rot, p.c);
      });
    return;
  }
  if (aWhy === 'move' || aWhy === 'reset') {
    S
      .select(aG)
      .transition()
      .duration(aWhy === 'reset' ? 150 : 0)
      .attr('transform', endTransform);
    return;
  }
  aG.setAttribute('transform', endTransform);
}
/** 中身がパーツの g に拡大を付けるときの動き（拡大・縮小のボタンは 150ms、duration 0 ならすぐ）。読み込みなどはその場で */
export function coreZoomTransform(aNode: Element, aValue: string | null, aWhy: string, _aCategory: unknown, aScale: number, info: any): void {
  const e = E();
  if (aWhy === 'zoom') {
    const transform = draw().zoomTransform(aScale, e.SIZE_CHARAT_IMAGE, e.SIZE_CHARAT_IMAGE);
    if (info.duration === 0) aNode.setAttribute('transform', transform);
    else
      S
        .select(aNode)
        .transition()
        .duration(info.duration == undefined ? 150 : info.duration)
        .attr('transform', transform);
    return;
  }
  if (aValue === null) aNode.removeAttribute('transform');
  else aNode.setAttribute('transform', aValue);
}
/** 全レイヤーを初めの値に（属性・位置を戻して中身を消す）。中身で組み立てる前（カテゴリの無いレイヤーも） */
export function refreshLayers(): void {
  const e = E();
  const sel = e.DIV_F_MAKE + " g[id^='" + e.ATTR_LAYER + "']";
  const scx = e.scMake.x(e.SIZE_CHARAT_IMAGE) / e.SIZE_CHARAT_IMAGE, scy = e.scMake.y(e.SIZE_CHARAT_IMAGE) / e.SIZE_CHARAT_IMAGE;
  S
    .selectAll(sel)
    .attr('paramParts', '')
    .attr('paramTx', 0)
    .attr('paramTy', 0)
    .attr('paramScx', scx)
    .attr('paramScy', scy)
    .attr('paramRev', 0)
    .attr('paramScale', 1)
    .attr('paramCol', '')
    .attr('paramColFrom', '')
    .attr('paramColTo', '')
    .attr('paramColLFrom', '')
    .attr('paramColLTo', '')
    .attr('paramColRFrom', '')
    .attr('paramColRTo', '')
    .attr('paramPtn', '')
    .attr('paramPrev', 0)
    .attr('paramPtnMode', 0)
    .attr('paramRotate', 0)
    .attr('paramOpacity', 1)
    .attr('transform', () => 'translate(0, 0), rotate(0,0,0), scale(' + scx + ',' + scy + ')');
  if (e.STROKE_WIDTH) S.selectAll(sel).attr('paramSW', '');
  S.selectAll(sel).selectAll('*').remove();
}
/** 保存データ（SAVE_FIELDS の並び）を共通の並びに（線の太さ paramSW は落ちる＝applySaveStroke で付ける） */
export function toStdSave(aChara: any): unknown {
  return { v: aChara.v, n: aChara.n, c: (aChara.c || []).map((charaLine: unknown) => E().toStdSaveLine(String(charaLine).split(',')).join(',')) };
}
/** 第1世代の柄（mono）: 保存データの柄・色の入れかえを画面ファイルの setPartsColorPattern で掛ける（最初の行・パーツがあるカテゴリだけ。先頭のレイヤーに値を入れてから） */
export function applyEnginePatterns(aChara: any): void {
  const e = E();
  const loaded: Record<string, boolean> = {};
  (aChara.c || []).forEach((charaLine: unknown) => {
    const cparam = e.SAVE_FIELDS ? e.toStdSaveLine(String(charaLine).split(',')) : String(charaLine).split(',');
    const category = e.allCategory[cparam[0]];
    if (!category || loaded[cparam[0]] || cparam.length < 2) return;
    loaded[cparam[0]] = true;
    if (e.getPartsLayerList(cparam[0], e.partsCache[category.NAME], cparam[1]).length == 0) return;
    const pCol = cparam.length > 2 ? cparam[2] : '';
    const pPtn = cparam.length > 13 ? cparam[13] : '';
    const pPrev = cparam.length > 14 && cparam[14] != '' ? cparam[14] : 0;
    const pPtnMode = cparam.length > 15 && cparam[15] != '' ? cparam[15] : 0;
    const svgArea = category.LAYER[0] == e.PARTS_BG ? e.svgBg : e.svgMake;
    svgArea.select('#' + e.ATTR_LAYER + category.LAYER[0]).attr('paramPtn', pPtn).attr('paramPrev', pPrev).attr('paramPtnMode', pPtnMode);
    e.setPartsColorPattern(svgArea, category, pPtn, pPrev, pPtnMode, pCol);
  });
}
/** 保存データの線の太さを付ける（最初の行・パーツがあるカテゴリだけ、先頭のレイヤーに paramSW、全レイヤーの線に） */
export function applySaveStroke(aChara: any): void {
  const e = E();
  const swIndex = e.SAVE_FIELDS ? e.SAVE_FIELDS.indexOf('paramSW') + 1 : 0;
  if (swIndex <= 0) return;
  const loaded: Record<string, boolean> = {};
  (aChara.c || []).forEach((charaLine: unknown) => {
    const cparam = String(charaLine).split(',');
    const category = e.allCategory[cparam[0]];
    if (!category || loaded[cparam[0]]) return;
    loaded[cparam[0]] = true;
    const pSW = cparam.length > swIndex ? cparam[swIndex] : '';
    if (pSW == '' || category.LAYER[0] == e.PARTS_BG || cparam.length < 2) return;
    if (e.getPartsLayerList(cparam[0], e.partsCache[category.NAME], cparam[1]).length == 0) return;
    e.svgMake.select('#' + e.ATTR_LAYER + category.LAYER[0]).attr('paramSW', pSW);
    C().setStrokeWidth(e.svgMake, category, e.ATTR_LAYER, pSW);
  });
}
/**
 * 手の排他（kawaii の excHands・似顔絵の handflg）を、前のエンジンと同じ順番で掛け直す（ランダムはカテゴリの順、読み込みは保存データの行の順。パーツの無い行は飛ばす）。
 * 先頭レイヤーのパーツ番号を入れながら 1 つずつ updateHandVisibility
 */
export function replayHandHook(aCore: any, aChara: any, aRnd: boolean): void {
  const e = E();
  if (!((typeof excHands !== 'undefined' && excHands) || (typeof handflg !== 'undefined' && handflg))) return;
  const state = aCore.state();
  const heads: Record<string, Element> = {};
  for (const key in e.allCategory) {
    const head = e.svgMake.select('#' + e.ATTR_LAYER + e.allCategory[key].LAYER[0]).node();
    if (head) {
      heads[key] = head;
      head.setAttribute('paramParts', '');
    }
  }
  const keys: string[] = [];
  if (aRnd) for (const k in e.allCategory) keys.push(k);
  else (aChara.c || []).forEach((charaLine: unknown) => keys.push(String(charaLine).split(',')[0]));
  const done: Record<string, boolean> = {};
  keys.forEach((k) => {
    const st = state[k];
    if (done[k] || !heads[k] || !st || st.parts == null) return;
    done[k] = true;
    heads[k].setAttribute('paramParts', String(st.parts));
    C().updateHandVisibility();
  });
  // 行に無かったカテゴリは中身の値に戻す（後処理が書きかえたものはそのまま）
  for (const k2 in heads) {
    const st2 = state[k2];
    if (!done[k2] && st2 && st2.parts != null && heads[k2].getAttribute('paramParts') === '') heads[k2].setAttribute('paramParts', String(st2.parts));
  }
}
/** 中身でキャラを組み立てる（保存データかランダム）。名前・切り抜き・はじめのカテゴリはここで */
export function makeCharactorCore(aCore: any, aChara: any, aRnd: boolean): void {
  const e = E();
  e.refreshLayers();
  if (e.MODE_TEXT) e.btnMenuName.SET('');
  if (aRnd) aCore.random();
  else {
    aCore.load(e.SAVE_FIELDS ? e.toStdSave(aChara) : aChara);
    if (e.STROKE_WIDTH) e.applySaveStroke(aChara);
    if (e.setPartsColorPattern !== e.setPartsColorPatternStd) e.applyEnginePatterns(aChara);
  }
  // 前のエンジンは読み込みでカテゴリごとに changeParts の後処理を通っていた。中身は通らないので、組み立てたあとに 1 回掛ける（2026-10-24、段階 B5）
  e.replayHandHook(aCore, aChara, aRnd);
  C().afterChangeParts();
  // 後処理が書きかえた先頭レイヤーのパーツ番号を中身へ写し、回転しているカテゴリは後処理で隠れた要素を除いた範囲で回し直す
  aCore.classic.syncParts();
  aCore.classic.replaceRotated();
  if (e.PARTS_MASK != '') e.applyMask(e.svgMake, e.ATTR_LAYER);
  if (!aRnd && aChara.n.length >= 2) e.btnMenuName.SET(e.config('NAME_INPUT', true) ? decodeURIComponent(aChara.n[0]) : '', aChara.n[1]);
  if (e.MODE_TEXT) {
    const pid = jq.attr('#' + e.ATTR_LAYER + e.PARTS_TEXT, 'paramParts');
    e.btnMenuName.SETMODE(pid == '' ? e.MODE_TEXT_VALID_MIN - 1 : pid);
  }
  fx.d3ClickCall('#chibiCategoryBtn0');
  if (!e.config('PARTS_LIST_BY_BUTTON', false) && e.config('LOAD_TOGGLES_PARTS', true)) e.btnEditParts.SW_PARTS();
}
/** 保存データ aChara か、ランダム（aRnd）でキャラを組み立てる（aRefresh は前の名残） */
export function makeCharactor(aChara: unknown, aRnd: boolean, _aRefresh?: boolean): void {
  const c = E().core();
  if (!c) return;
  E().makeCharactorCore(c, aChara, aRnd);
}

// ---- 全体の反転・全画面・調べる用 ----

/**
 * キャラ全体の左右反転。第1世代 b（前の gen1b.js）は 2 つの版: REVERSE_ALL_ALIGN（profile・run）は反転できる（IS_REV 2）全カテゴリを aReverse の向きにそろえる（REVERSE_ALL_DURATION）、
 * それ以外（back・front・front2 など）は反転できる（IS_REV 1 以上）全カテゴリを今の向きの反対へ 10ms ずつずらして（引数は除くレイヤーの並びだけ）。
 * ほかの画面は IS_REV 2 の全カテゴリを reverse の向きに 220ms（jakigan）
 */
export function makeReverseAll(): (...args: any[]) => void {
  const e = E();
  if (screen() === 'gen1b' && !e.config('REVERSE_ALL_ALIGN', false)) {
    return (aExclude: string[]) => {
      const en = E();
      let dcnt = 0;
      for (const key in en.allCategory) {
        const cat = en.allCategory[key];
        if (aExclude.indexOf(cat.LAYER[0]) < 0 && cat.IS_REV >= 1) {
          C().reverseG(en.svgMake, cat, en.ATTR_LAYER, !C().getReverseG(cat, en.ATTR_LAYER), en.scMake.x, en.scMake.y, en.SIZE_CHARAT_IMAGE, dcnt, 100);
          dcnt += 10;
        }
      }
    };
  }
  const gen1b = screen() === 'gen1b';
  return (reverse: unknown, aExclude: string[]) => {
    const en = E();
    const dcnt = 0;
    for (const key in en.allCategory) {
      const cat = en.allCategory[key];
      if (aExclude.indexOf(cat.LAYER[0]) < 0 && cat.IS_REV == 2) {
        C().reverseG(en.svgMake, cat, en.ATTR_LAYER, reverse, en.scMake.x, en.scMake.y, en.SIZE_CHARAT_IMAGE, dcnt, gen1b ? en.config('REVERSE_ALL_DURATION', 100) : 220);
      }
    }
  };
}
/** ページ全体を全画面に（ブラウザごとの接頭辞の版も） */
export function enterFullscreen(): void {
  const doc = document.documentElement as HTMLElement & Record<string, any>;
  if (doc.requestFullscreen) doc.requestFullscreen();
  else if (doc.mozRequestFullScreen) doc.mozRequestFullScreen();
  else if (doc.webkitRequestFullscreen) doc.webkitRequestFullscreen();
  else if (doc.msRequestFullscreen) doc.msRequestFullscreen();
}
/** 調べる用の文字を出す */
export function debug(aMes: string): void {
  if (jq.els(E().DIV_F_DEBUG).length > 0) jq.setText(E().DIV_F_DEBUG, aMes);
}
